import {
  changeVehicleGroupAssignments,
  clearVehicles,
  createVehicle,
  editVehicle,
  importVehicles,
  loadVehicleOrphans,
  loadVehicleDetails,
  loadVehicles,
  loadVehiclesGroups,
  removeFromVehicles,
  removeSelectedVehicle,
  resetCurrentStep,
  saveCurrentStep,
  selectVehicle,
  setCreatingNewVehicle,
  setVehicleOrphans,
  setPrefilledVehicleId,
  setVehicleErrorMessage,
  setVehiclesOffset,
  setVehiclesQuery,
  setVehiclesRowsDescriptor,
  setVehiclesSort,
  triggerTableReload,
  clearImportResponse,
  loadLinkedData,
} from './vehicles.actions';
import {
  clearSelectedVehicle,
  clearVehicleStore,
  resetVehiclesStatus,
  trackVehiclesRequestsStatus,
  updateCreatingNewVehicle,
  updateCurrentStepReset,
  updateCurrentStepSave,
  updateDeleteVehicleStatus,
  updateHasVehicles,
  updateImportResponse,
  updateLinkedData,
  updateOffset,
  updateOrphans,
  updatePrefilledVehicleId,
  updateQuery,
  updateSelectedVehicle,
  updateSelectedVehiclesGroupIds,
  updateSelectedVehiclesGroups,
  updateSort,
  updateTableReload,
  updateVehicleErrorMessage,
  updateVehicleImportStatus,
  updateVehicleRowsDescriptor,
  updateVehicleUpdateStatus,
  updateVehicles,
  updateVehiclesGroupsUpdateStatus,
  vehicleEntitiesSnapshot,
  vehicleStoreSnapshot,
} from './vehicles.repository';
import { reloadAssets } from '../assets/assets.actions';
import { vehicleIntegrationLinkedDataEnabled } from '../app-settings/app-settings.repository';
import { chimportVehicles } from '../utils/chimport-vehicles';
import { checkHasAssets } from '../ams/ams.actions';
import { VehiclesImportResponse } from '../../models/vehicles-import-response';
import { EMPTY_ROWS_DESCRIPTOR } from '../../models/empty-rows-descriptor';
import { boolDefinedAndTrue } from '../../utils/bool-defined-and-true';
import { extractAndHandleError } from '../../utils/extract-and-handle-error';
import { getErrorContext } from '../../utils/get-error-context';
import { reportToTrackjs } from '../../utils/report-to-trackjs';
import { updateToast } from '../toasts.repository';
import { translate } from '@jsverse/transloco';
import {
  FleetApi,
  FleetVehicleInformation,
  OrphanedResource,
  Vehicle,
  VehicleDriverLink,
} from '@tmx/data-access';
import { switchMap, tap, iif } from 'rxjs';
import {
  createEffect,
  dispatch,
  ofType,
  registerEffects,
  removeEffects,
  tapResult,
} from '@ngneat/effects';
import {
  LoadingRowsDescriptor,
  RowDescriptor,
  SelectAllState,
  UpdateRowsDescriptor,
} from '@tmx/operandi';

const TRACKJS_SOURCE = 'vehicle effects';
const API = 'fleet';

function displayVehicleError(error: string) {
  dispatch(
    setVehicleErrorMessage({
      error,
    })
  );
}

const loadVehicles$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadVehicles),
    switchMap(({ params }) =>
      FleetApi.getVehicles(params).pipe(trackVehiclesRequestsStatus('vehicles'))
    ),
    tapResult(
      ({ data, size, unfilteredSize }) => {
        updateHasVehicles(unfilteredSize > 0);
        if (vehicleIntegrationLinkedDataEnabled()) {
          dispatch(
            loadLinkedData({
              vehicleIds: data.map((vehicle) => vehicle.vehicleId),
            })
          );
        }
        updateVehicles(data, size);
      },
      (error) => {
        reportToTrackjs(
          TRACKJS_SOURCE,
          'Error loading vehicles',
          getErrorContext(error)
        );
        extractAndHandleError(error, API, displayVehicleError);
      }
    )
  )
);

const loadLinkedData$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadLinkedData),
    switchMap(({ vehicleIds }) =>
      FleetApi.postVehiclesDriverLinks(vehicleIds).pipe(
        trackVehiclesRequestsStatus('linked-data'),
        tapResult(({ data }) => {
          const linkedData = new Map<string, VehicleDriverLink>();
          for (const link of data.vehicleDriverLinkDetails) {
            linkedData.set(link.vehicleId, link);
          }
          updateLinkedData(linkedData);
        })
      )
    )
  )
);

const selectVehicle$ = createEffect((actions) =>
  actions.pipe(
    ofType(selectVehicle),
    tap(({ vehicle }) => {
      updateSelectedVehicle(vehicle);
    })
  )
);

const loadVehiclesGroups$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadVehiclesGroups),
    switchMap(({ vehicleId }) =>
      FleetApi.getVehiclesIdGroups(vehicleId).pipe(
        trackVehiclesRequestsStatus('vehicles-groups-ids'),
        tapResult(({ data }) => {
          updateSelectedVehiclesGroups(data);
          updateSelectedVehiclesGroupIds(
            data.map((vehicleGroup) => vehicleGroup.vehicleGroupId)
          );
        })
      )
    )
  )
);

export const changeVehicleGroupAssignments$ = createEffect((actions) =>
  actions.pipe(
    ofType(changeVehicleGroupAssignments),
    tap(() => {
      dispatch(setVehicleErrorMessage({ error: null }));
    }),
    switchMap(
      ({ assignmentVehicleGroupIds, unassignmentVehicleGroupIds, id }) => {
        return FleetApi.postVehiclesVehicleGroupsCombinedAssignment(
          id,
          assignmentVehicleGroupIds,
          unassignmentVehicleGroupIds
        ).pipe(
          trackVehiclesRequestsStatus('edit-vehicles-groups'),
          tapResult(
            () => {
              updateVehiclesGroupsUpdateStatus();
              const currentIds =
                vehicleStoreSnapshot().selectedVehiclesGroupIds;
              currentIds.push(...assignmentVehicleGroupIds);
              const updatedIds = currentIds.filter(
                (currentId) => !unassignmentVehicleGroupIds.includes(currentId)
              );
              updateSelectedVehiclesGroupIds(updatedIds);
              dispatch(triggerTableReload());
              updateToast({
                type: 'success',
                content: translate(
                  'cfs.addEditVehicleModal.toastSuccessUpdateVehicle'
                ),
              });
            },
            (error: Response) => {
              reportToTrackjs(
                TRACKJS_SOURCE,
                'Error assigning vehicle group',
                getErrorContext(error)
              );
              extractAndHandleError(error, API, displayVehicleError);
            }
          )
        );
      }
    )
  )
);

const loadVehicleDetails$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadVehicleDetails),
    switchMap(({ vehicleId }) =>
      FleetApi.getVehiclesId(vehicleId as unknown as string).pipe(
        trackVehiclesRequestsStatus('vehicle-details'),
        tapResult(
          (response) => {
            const fleetVehicleInformation = {
              appVersion: response.appVersion,
              dataVersion: response.dataVersion,
              lastConnected: response.lastConnected,
              lastCommunicationRequest: response.lastCommunicationRequest,
              lastStoredTimeStamp: response.lastStoredTimeStamp,
            } as FleetVehicleInformation;
            updateSelectedVehicle(response as Vehicle, fleetVehicleInformation);
          },
          (error) => {
            reportToTrackjs(
              TRACKJS_SOURCE,
              'Error loading vehicle details',
              getErrorContext(error)
            );
            updateToast({
              type: 'danger',
              content: translate(
                'cfs.addEditVehicleModal.toastErrorLoadingVehicleDetails'
              ),
            });
          }
        )
      )
    )
  )
);

const createVehicle$ = createEffect((actions) =>
  actions.pipe(
    ofType(createVehicle),
    switchMap(({ vehicle, skipSelection }) =>
      FleetApi.postVehicles(vehicle).pipe(
        trackVehiclesRequestsStatus('vehicle-update'),
        tapResult(
          (response) => {
            const { data } = response;

            const selectedVehicle = boolDefinedAndTrue(skipSelection)
              ? null
              : data[0];
            if (!vehicleStoreSnapshot().hasVehicles) {
              dispatch(checkHasAssets());
            }
            updateSelectedVehicle(selectedVehicle);
            updateVehicleUpdateStatus();
            resetVehiclesStatus();
            dispatch(triggerTableReload());
            dispatch(reloadAssets());
            updateToast({
              type: 'success',
              content: translate(
                'cfs.addEditVehicleModal.toastSuccessCreateVehicle'
              ),
            });
          },
          (error) => {
            reportToTrackjs(
              TRACKJS_SOURCE,
              'Error creating vehicle',
              getErrorContext(error)
            );
            extractAndHandleError(error, API, displayVehicleError);
          }
        )
      )
    )
  )
);

const importVehicles$ = createEffect((actions) =>
  actions.pipe(
    ofType(importVehicles),
    trackVehiclesRequestsStatus('vehicle-import'),
    tap(() => {
      dispatch(clearImportResponse());
    }),
    chimportVehicles(TRACKJS_SOURCE),
    tapResult((importResponse) => {
      updateVehicleImportStatus();
      updateImportResponse(importResponse);
      if (importResponse.imported > 0) {
        if (!vehicleStoreSnapshot().hasVehicles) {
          dispatch(checkHasAssets());
        }
        dispatch(triggerTableReload());
      }
    })
  )
);

const clearImportResponse$ = createEffect((actions) =>
  actions.pipe(
    ofType(clearImportResponse),
    tap(() => {
      updateImportResponse(new VehiclesImportResponse());
    })
  )
);

const editVehicle$ = createEffect((actions) =>
  actions.pipe(
    ofType(editVehicle),
    switchMap(({ vehicle, integrationType, skipSelection }) =>
      iif(
        () => integrationType === 'VehicleId',
        FleetApi.putVehicles(vehicle).pipe(
          trackVehiclesRequestsStatus('vehicle-update')
        ),
        FleetApi.putVehiclesUpdateByIds(vehicle).pipe(
          trackVehiclesRequestsStatus('vehicle-update')
        )
      ).pipe(
        tapResult(
          (response) => {
            const { data } = response;

            const selectedVehicle = boolDefinedAndTrue(skipSelection)
              ? null
              : data[0];
            updateSelectedVehicle(selectedVehicle as Vehicle);
            updateVehicleUpdateStatus();
            resetVehiclesStatus();
            dispatch(triggerTableReload());
            dispatch(reloadAssets());
            updateToast({
              type: 'success',
              content: translate(
                'cfs.addEditVehicleModal.toastSuccessUpdateVehicle'
              ),
            });
          },
          (error) => {
            reportToTrackjs(
              TRACKJS_SOURCE,
              'Error updating vehicle',
              getErrorContext(error)
            );
            extractAndHandleError(error, API, displayVehicleError);
          }
        )
      )
    )
  )
);

const setCreatingNewVehicle$ = createEffect((actions) =>
  actions.pipe(
    ofType(setCreatingNewVehicle),
    tap(({ creatingNewVehicle }) => {
      updateCreatingNewVehicle(creatingNewVehicle);
    })
  )
);

const setPrefilledVehicleId$ = createEffect((actions) =>
  actions.pipe(
    ofType(setPrefilledVehicleId),
    tap(({ id }) => {
      updatePrefilledVehicleId(id);
    })
  )
);

const loadVehicleOrphans$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadVehicleOrphans),
    switchMap(() =>
      FleetApi.getCompanyFleetsOrphan().pipe(
        tapResult(
          (orphanedVehicles) => {
            const orphans = new Map<string, OrphanedResource>();
            for (const vehicle of orphanedVehicles) {
              orphans.set(vehicle.vehicleId ?? '', vehicle);
            }
            updateOrphans(orphans);
          },
          (error: Response) => {
            if (error.status === 404) {
              updateOrphans(new Map<string, OrphanedResource>());
            } else {
              reportToTrackjs(
                TRACKJS_SOURCE,
                'Error loading orphans',
                getErrorContext(error)
              );
              extractAndHandleError(error, API, displayVehicleError);
            }
          }
        )
      )
    )
  )
);

const setVehicleOrphans$ = createEffect((actions) =>
  actions.pipe(
    ofType(setVehicleOrphans),
    tap(({ orphans }) => {
      updateOrphans(orphans);
    })
  )
);

const resetCurrentStep$ = createEffect((actions) =>
  actions.pipe(
    ofType(resetCurrentStep),
    tap(({ currentStep }) => {
      updateCurrentStepReset(currentStep);
    })
  )
);

const saveCurrentStep$ = createEffect((actions) =>
  actions.pipe(
    ofType(saveCurrentStep),
    tap(({ currentStep }) => {
      updateCurrentStepSave(currentStep);
    })
  )
);

const removeFromVehicle$ = createEffect((actions) =>
  actions.pipe(
    ofType(removeFromVehicles),
    switchMap(({ vehicleId }) => {
      return FleetApi.deleteVehicles(encodeURIComponent(vehicleId)).pipe(
        trackVehiclesRequestsStatus('delete-vehicle'),
        tapResult(
          () => {
            updateToast({
              type: 'success',
              content: translate('cfs.vehicles.toastSuccessDeleteVehicle'),
            });
            updateDeleteVehicleStatus();
            dispatch(triggerTableReload());
          },
          (error) => {
            reportToTrackjs(
              TRACKJS_SOURCE,
              'Error deleting Vehicle',
              getErrorContext(error)
            );
            extractAndHandleError(error, API, displayVehicleError);
          }
        )
      );
    })
  )
);

const clearVehicles$ = createEffect((actions) =>
  actions.pipe(
    ofType(clearVehicles),
    tapResult(() => {
      clearVehicleStore();
    })
  )
);

const removeSelectedVehicle$ = createEffect((actions) =>
  actions.pipe(
    ofType(removeSelectedVehicle),
    tapResult(() => {
      clearSelectedVehicle();
    })
  )
);

const setVehiclesOffset$ = createEffect((actions) =>
  actions.pipe(
    ofType(setVehiclesOffset),
    tap(({ offset }) => {
      updateOffset(offset);
    })
  )
);

const setVehiclesSort$ = createEffect((actions) =>
  actions.pipe(
    ofType(setVehiclesSort),
    tap(({ sortExpression, sortDirection }) => {
      updateSort(sortExpression, sortDirection);
    })
  )
);

const setVehiclesQuery$ = createEffect((actions) =>
  actions.pipe(
    ofType(setVehiclesQuery),
    tap(({ query }) => {
      updateQuery(query);
    })
  )
);

const setVehiclesRowsDescriptor$ = createEffect((actions) =>
  actions.pipe(
    ofType(setVehiclesRowsDescriptor),
    tap(({ loading }) => {
      if (loading) {
        updateVehicleRowsDescriptor(new LoadingRowsDescriptor());
        return;
      }
      const { totalVehicles, offset } = vehicleStoreSnapshot();
      const vehicles = vehicleEntitiesSnapshot();
      const rows = vehicles.map(
        (vehicle) => new RowDescriptor(vehicle, (row) => row.data.id)
      );
      updateVehicleRowsDescriptor(
        totalVehicles === 0
          ? EMPTY_ROWS_DESCRIPTOR
          : new UpdateRowsDescriptor(
              rows,
              offset,
              totalVehicles,
              0,
              SelectAllState.Deselected
            )
      );
    })
  )
);

const setVehicleErrorMessage$ = createEffect((actions) =>
  actions.pipe(
    ofType(setVehicleErrorMessage),
    tap(({ error }) => {
      const { creatingNewVehicle, selectedVehicle } = vehicleStoreSnapshot();
      if (error !== null) {
        if (!selectedVehicle && !creatingNewVehicle) {
          updateToast({ type: 'danger', content: error });
        }
      }
      updateVehicleErrorMessage(error);
    })
  )
);

const triggerTableReload$ = createEffect((actions) =>
  actions.pipe(
    ofType(triggerTableReload),
    tap(() => {
      updateTableReload();
    })
  )
);

const effects = [
  loadVehicles$,
  loadLinkedData$,
  selectVehicle$,
  removeFromVehicle$,
  clearVehicles$,
  importVehicles$,
  clearImportResponse$,
  saveCurrentStep$,
  setCreatingNewVehicle$,
  setPrefilledVehicleId$,
  loadVehicleOrphans$,
  setVehicleOrphans$,
  resetCurrentStep$,
  loadVehicleDetails$,
  loadVehiclesGroups$,
  changeVehicleGroupAssignments$,
  createVehicle$,
  editVehicle$,
  setVehicleErrorMessage$,
  removeSelectedVehicle$,
  setVehiclesOffset$,
  setVehiclesSort$,
  setVehiclesQuery$,
  setVehiclesRowsDescriptor$,
  triggerTableReload$,
];

export function registerVehiclesEffects() {
  registerEffects(effects);
}

export function deregisterVehiclesEffects() {
  removeEffects(effects);
}
